import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Checkbox,
  FormGroup,
  Snackbar,
  Alert,
  CircularProgress,
  alpha,
  useTheme
} from '@mui/material';
import { buildCrmApiUrl } from '../../api/apiConfig';

const RESEARCH_PLAN_ASSETS = ['Stock', 'Option', 'Future', 'Multibagger'];
const ALGO_ASSETS = ['stock1', 'stock2', 'stock3', 'stock4', 'stock5', 'option1', 'option5', 'future4', 'future5', 'option6', 'future6', 'option7', 'future7', 'starter','plus','elite','black'];

const PlanActivation = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [rplans, setRplans] = useState([]);
  const [rplansLoading, setRplansLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [type, setType] = useState('research'); // 'research' | 'algo'
  const [researchMode, setResearchMode] = useState('plan'); // 'plan' | 'assets'
  const [number, setNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [accessMonths, setAccessMonths] = useState(1);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [selectedResearchAssets, setSelectedResearchAssets] = useState([]);
  const [selectedAlgoAssets, setSelectedAlgoAssets] = useState([]);
  const [algoLots, setAlgoLots] = useState({}); // { asset: lotCount }

  const num = localStorage.getItem('number');

  const fetchRplans = () => {
    if (!num) return;
    setRplansLoading(true);
    fetch(buildCrmApiUrl(`plan-activation/rplans?number=${encodeURIComponent(num)}`), {
      headers: { 'Authorization': 'dskjhvfgfg46953u0y5y50y90y' }
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.data)) {
          setRplans(d.data);
          if (d.data.length > 0 && !selectedPlanId) {
            setSelectedPlanId(d.data[0].plan_id);
            setSelectedPlanName(d.data[0].name);
          }
        }
      })
      .catch(() => setSnackbar({ open: true, message: 'Failed to load plans', severity: 'error' }))
      .finally(() => setRplansLoading(false));
  };

  useEffect(() => {
    if (type === 'research' && researchMode === 'plan') {
      fetchRplans();
    }
  }, [type, researchMode]);

  const handleResearchAssetToggle = (asset) => {
    setSelectedResearchAssets((prev) =>
      prev.includes(asset) ? prev.filter((a) => a !== asset) : [...prev, asset]
    );
  };

  const handleAlgoAssetToggle = (asset) => {
    setSelectedAlgoAssets((prev) =>
      prev.includes(asset) ? prev.filter((a) => a !== asset) : [...prev, asset]
    );
    if (!selectedAlgoAssets.includes(asset)) {
      setAlgoLots((p) => ({ ...p, [asset]: 1 }));
    } else {
      setAlgoLots((p) => {
        const next = { ...p };
        delete next[asset];
        return next;
      });
    }
  };

  const handleAlgoLotChange = (asset, value) => {
    const v = parseInt(value, 10);
    setAlgoLots((p) => ({ ...p, [asset]: isNaN(v) || v < 1 ? 1 : v }));
  };

  const handlePlanSelect = (e) => {
    const planId = e.target.value;
    const plan = rplans.find((p) => p.plan_id === planId);
    setSelectedPlanId(planId);
    setSelectedPlanName(plan ? plan.name : '');
  };

  const handleSubmit = async () => {
    if (!num) {
      setSnackbar({ open: true, message: 'Please log in', severity: 'error' });
      return;
    }
    if (!number?.trim()) {
      setSnackbar({ open: true, message: 'Mobile number is required', severity: 'error' });
      return;
    }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 0) {
      setSnackbar({ open: true, message: 'Valid amount is required', severity: 'error' });
      return;
    }
    const months = parseInt(accessMonths, 10);
    if (isNaN(months) || months < 1) {
      setSnackbar({ open: true, message: 'Access months must be at least 1', severity: 'error' });
      return;
    }

    let payload = {
      type: type,
      number: number.trim(),
      amount: amt,
      access_months: months
    };

    if (type === 'research') {
      if (researchMode === 'plan') {
        if (!selectedPlanId || !selectedPlanName) {
          setSnackbar({ open: true, message: 'Select a plan', severity: 'error' });
          return;
        }
        payload.plan_id = selectedPlanId;
        payload.plan_name = selectedPlanName;
      } else {
        if (selectedResearchAssets.length === 0) {
          setSnackbar({ open: true, message: 'Select at least one asset', severity: 'error' });
          return;
        }
        payload.plan_assets = selectedResearchAssets;
      }
    } else {
      if (selectedAlgoAssets.length === 0) {
        setSnackbar({ open: true, message: 'Select at least one asset', severity: 'error' });
        return;
      }
      payload.plan_assets = selectedAlgoAssets;
      payload.plan_assets_lots = selectedAlgoAssets.map((a) => algoLots[a] ?? 1);
    }

    setLoading(true);
    try {
      const res = await fetch(buildCrmApiUrl(`plan-activation/activate?number=${encodeURIComponent(num)}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'dskjhvfgfg46953u0y5y50y90y'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setSnackbar({ open: true, message: data.data?.message || 'Plan activated successfully', severity: 'success' });
        setNumber('');
        setAmount('');
        setAccessMonths(1);
        setSelectedResearchAssets([]);
        setSelectedAlgoAssets([]);
        setAlgoLots({});
      } else {
        setSnackbar({ open: true, message: data.error || 'Activation failed', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Request failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Plan Activation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Activate research or algo plans for users. Admin only.
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`
        }}
      >
        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Plan Type</FormLabel>
          <RadioGroup row value={type} onChange={(e) => setType(e.target.value)}>
            <FormControlLabel value="research" control={<Radio />} label="Research" />
            <FormControlLabel value="algo" control={<Radio />} label="Algo" />
          </RadioGroup>
        </FormControl>

        {type === 'research' && (
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Research Mode</FormLabel>
            <RadioGroup row value={researchMode} onChange={(e) => setResearchMode(e.target.value)}>
              <FormControlLabel value="plan" control={<Radio />} label="Plan (from rplans)" />
              <FormControlLabel value="assets" control={<Radio />} label="Individual Assets" />
            </RadioGroup>
          </FormControl>
        )}

        {type === 'research' && researchMode === 'plan' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Plan</InputLabel>
            <Select
              value={selectedPlanId}
              label="Plan"
              onChange={handlePlanSelect}
              disabled={rplansLoading}
            >
              {rplansLoading && <MenuItem value="">Loading...</MenuItem>}
              {rplans.map((p) => (
                <MenuItem key={p.plan_id} value={p.plan_id}>
                  {p.name} ({p.plan_id?.slice(0, 12)}...)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {type === 'research' && researchMode === 'assets' && (
          <Box sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ mb: 1, display: 'block' }}>
              Select Assets (multiple)
            </FormLabel>
            <FormGroup row>
              {RESEARCH_PLAN_ASSETS.map((asset) => (
                <FormControlLabel
                  key={asset}
                  control={
                    <Checkbox
                      checked={selectedResearchAssets.includes(asset)}
                      onChange={() => handleResearchAssetToggle(asset)}
                    />
                  }
                  label={asset}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {type === 'algo' && (
          <Box sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ mb: 1, display: 'block' }}>
              Select Algo Assets (multiple)
            </FormLabel>
            <FormGroup row sx={{ gap: 1, flexWrap: 'wrap' }}>
              {ALGO_ASSETS.map((asset) => (
                <Box key={asset} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedAlgoAssets.includes(asset)}
                        onChange={() => handleAlgoAssetToggle(asset)}
                      />
                    }
                    label={asset}
                  />
                  {selectedAlgoAssets.includes(asset) && (
                    <TextField
                      type="number"
                      size="small"
                      label="Lots"
                      value={algoLots[asset] ?? 1}
                      onChange={(e) => handleAlgoLotChange(asset, e.target.value)}
                      inputProps={{ min: 1, style: { width: 50 } }}
                      sx={{ width: 80 }}
                    />
                  )}
                </Box>
              ))}
            </FormGroup>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
          <TextField
            fullWidth
            label="Mobile Number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="e.g. 8879102860"
          />
          <TextField
            fullWidth
            type="number"
            label="Amount (₹)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Access Months"
            value={accessMonths}
            onChange={(e) => setAccessMonths(e.target.value)}
            inputProps={{ min: 1 }}
          />
        </Box>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ mt: 3 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Activate Plan'}
        </Button>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanActivation;
